<template>
  <div class="view" v-if="canDelete || canEdit" v-cloak>
    <GridPanelHeader :title="title" :grid-ref="$refs.workInputsGrid" :show-add="false" :show-filter="true" />
    <GridFilter :grid-ref="$refs.workInputsGrid" :visible.sync="filterPanelVisible" :title="$t('common.filter')">
      <DateRangeFilter :dateFrom.sync="filters.work_min_date" :dateTo.sync="filters.work_max_date" />
    </GridFilter>
    <div class="content-body pt-1">
      <div class="row">
        <div id="toast-msg"></div>
      </div>
      <DxDataGrid
        ref="workInputsGrid"
        :data-source="inputsDataSource"
        :selected-row-keys="selectedInputUuids"
        :column-min-width="110"
        style="min-height: 350px"
        :show-column-lines="true"
        :show-borders="true"
        :column-auto-width="true"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :row-alternation-enabled="true"
        :remote-operations="gridRemoteOperations"
        :element-attr="gridAttributes"
        @option-changed="onOptionChanged"
        @content-ready="onContentReady"
        @row-click="onRowClick"
        @selection-changed="onSelectionChanged"
      >
        <DxSelection mode="multiple" showCheckBoxesMode="always" selectAllMode="page" />
        <DxColumn
          :caption="$t('works.inputs.work_interval')"
          data-type="string"
          :allow-sorting="false"
          :allow-filtering="false"
          :calculate-cell-value="getWorkIntervalValue"
          cell-template="workIntervalTemplate"
        />
        <template #workIntervalTemplate="{ data }">
          <span>{{ getWorkIntervalText(data.data) }}</span>
        </template>
        <DxColumn
          data-field="created_at"
          :caption="$t('common.created_at')"
          data-type="date"
          :format="getDxGridDefaultDateFormat + ' HH:mm'"
          :allow-sorting="false"
          :filter-operations="['contains']"
        />
        <DxColumn
          data-field="related_works_names"
          :caption="$t('common.name')"
          data-type="string"
          :allow-sorting="false"
          :filter-operations="['contains']"
        />
        <DxColumn
          data-field="product"
          :caption="$t('common.product_name')"
          data-type="string"
          :allow-sorting="false"
          :filter-operations="['contains']"
        />
        <DxColumn
          data-field="quantity"
          :caption="$t('common.quantity')"
          data-type="number"
          :allow-sorting="false"
          :filter-operations="['=']"
          format="#,##0.####"
        />
        <DxColumn
          data-field="measure_unit_symbol"
          :caption="$t('common.unit')"
          data-type="string"
          :allow-sorting="false"
          :filter-operations="['contains']"
        />
        <DxColumn
          data-field="dose_per_ha"
          :caption="$t('works.inputs.dose_ha')"
          data-type="number"
          :allow-sorting="false"
          :filter-operations="['=']"
          format="#,##0.####"
        />

        <DxColumn
          :width="60"
          alignment="center"
          :fixed="true"
          fixed-position="right"
          :allow-sorting="false"
          :allow-filtering="false"
          :show-in-column-chooser="false"
          cell-template="dynamicMenuCell"
        />
        <template #dynamicMenuCell="{ data }">
          <DxActionMenu
            :grid-ref="$refs.workInputsGrid"
            row-key="uuid"
            :row="data.data"
            :can-edit="false"
            :can-delete="canDelete"
            :allow-deleting="allowDeleting"
          />
        </template>

        <DxMasterDetail :enabled="true" template="affectedParcelsTemplate" />
        <template #affectedParcelsTemplate="{ data }">
          <div>
          <GridPanelHeader :title="$t('common.parcels')" :grid-ref="$refs.affectedParcelsGrid" :show-add="false" :show-filter="false" :show-export="false" :show-columns="false" />
          <DxDataGrid
            ref="affectedParcelsGrid"
            :data-source="getAffectedParcels(data.data)"
            :show-borders="true"
            :show-column-lines="true"
            :column-auto-width="true"
            :allow-column-resizing="true"
            :row-alternation-enabled="true"
            :element-attr="detailGridAttributes"
            :height="250"
          >
            <DxPaging :enabled="false" />
            <DxColumn
              data-field="parcel.name"
              :caption="$t('common.name')"
              data-type="string"
              :allow-sorting="false"
              :allow-filtering="false"
            />
            <DxColumn
              data-field="net_surface_worked"
              :caption="$t('common.surface')"
              data-type="number"
              :allow-sorting="false"
              :allow-filtering="false"
              alignment="left"
              format="#,##0.####"
            />
            <DxColumn
              data-field="_dose_value"
              :caption="$t('common.quantity')"
              data-type="number"
              :allow-sorting="false"
              :allow-filtering="false"
              alignment="left"
              format="#,##0.####"
            />
          </DxDataGrid>
          </div>
        </template>

        <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :use-icons="true" mode="row">
          <DxTexts
            :confirm-delete-message="
              $t('common.confirm_delete', {
                title: $t('works.inputs.name'),
              })
            "
            :confirm-delete-title="$t('common.confirm_delete_title')"
            :deleteRow="$t('common.delete')"
          />
        </DxEditing>

        <DxPaging :total="3500" :page-size="pageSize" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="pageSizes"
          display-mode="compact"
          :info-text="$t('common.page_info')"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
        />
        <DxFilterRow :visible="true" />
        <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
        <DxColumnChooser :enabled="false" mode="select" />
        <DxColumnFixing :enabled="true" />
        <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'Parcels'" />
      </DxDataGrid>
      <GridGroupActions
        ref="groupActions"
        :grid-ref="$refs.workInputsGrid"
        :selected-count="selectedInputUuidsAll.length"
        :show-delete-action="canDelete"
        :delete-uuids="selectedInputUuidsAll"
        :delete-handler="deleteSelectedInputs"
        @cleared="onSelectionCleared"
        @delete-success="onBulkDeleteSuccess"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { canDelete, canEdit } from "@/helpers/Permissions";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DateRangeFilter from "@/components/DxGrid/DateRangeFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import GridGroupActions from "@/components/DxGrid/GridGroupActions.vue";
import CustomStore from "devextreme/data/custom_store";
import {
  DxColumn,
  DxSelection,
  DxDataGrid,
  DxEditing,
  DxPager,
  DxPaging,
  DxTexts,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxMasterDetail,
  DxFilterRow,
  DxExport,
} from "devextreme-vue/data-grid";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format, parseISO, addMonths } from "date-fns";
import { dxFilterKeyExist } from "@/helpers/dxGridHelper";
import { bulkAction } from "@/helpers/generalActions";

export default {
  name: "WorkInputList",
  middleware: ["auth", "check-company-admin"],
  data() {
    const today = new Date();
    return {
      pageSize: 15,
      pageSizes: [15, 50, 100],
      filterPanelVisible: false,
      defaultFilters: {
        work_min_date: addMonths(today, -11),
        work_max_date: addMonths(today, 1),
      },
      filters: {
        work_min_date: addMonths(today, -11),
        work_max_date: addMonths(today, 1),
      },
      inputsDataSource: new CustomStore({
        key: "uuid",
        load: this.load,
        remove: this.remove,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      gridAttributes: {
        class: "smagria-grid work-inputs-grid",
      },
      detailGridAttributes: {
        class: "smagria-grid work-inputs-detail-grid",
      },
      selectedInputUuids: [],
      selectedInputUuidsAll: [],
      selectedInputRowsByUuid: {},
      grid_data_uuids: [],
      isRestoringSelection: false,
    };
  },
  components: {
    GridPanelHeader,
    GridFilter,
    DateRangeFilter,
    GridGroupActions,
    DxActionMenu,
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxPaging,
    DxPager,
    DxEditing,
    DxTexts,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxMasterDetail,
    DxFilterRow,
    DxExport,
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    title() {
      return this.$t("works.inputs.name");
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getInputs: "works-inputs/getInputs",
      deleteInput: "works-inputs/deleteInput",
    }),
    // Filter handlers
    callAfterResetFilters() {
      this.filters = { ...this.defaultFilters };
    },
    allowDeleting() {
      return this.canDelete;
    },
    getWorkIntervalText(row) {
      if (!row) return "";
      const start = this.formatGridDate(row.work_min_date);
      const end = this.formatGridDate(row.work_max_date);
      return start !== end ? `${start} - ${end}`.trim() : start;
    },
    formatGridDate(value) {
      if (!value) return "";
      const date = typeof value === "string" ? parseISO(value) : value;
      if (!date || Number.isNaN(date.getTime())) return "";
      return format(date, this.getDxGridDefaultDateFormat);
    },
    getWorkIntervalValue(row) {
      return this.getWorkIntervalText(row);
    },
    getAffectedParcels(row) {
      const parcels = Array.isArray(row?.affected_parcels) ? row.affected_parcels : [];
      const dose = Number(row?.dose_per_ha) || 0;
      return parcels.map((item) => {
        const surface = Number(item.net_surface_worked) || 0;
        return {
          ...item,
          _dose_value: dose * surface,
        };
      });
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        this.pageSize = e.value;
        const instance = this.$refs?.workInputsGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
          // Force a hard reload to drop any cached data after page size change.
          instance.getDataSource()?.reload?.();
        }
        return;
      }
    },
    async load(loadOptions) {
      let dataSource = {
        data: [],
        totalCount: 0,
      };

      const instance = this.$refs?.workInputsGrid?.instance;
      const uiPageSize = instance?.pageSize?.();
      const pageLimit = loadOptions.isLoadingAll ? 99999 : (uiPageSize || loadOptions.take);
      const pageNumber = (loadOptions.skip + pageLimit) / pageLimit;

      let normalizedFilter = loadOptions.filter;
      if (Array.isArray(normalizedFilter) && normalizedFilter.length === 3 && typeof normalizedFilter[0] === "string") {
        normalizedFilter = [normalizedFilter];
      }

      const payload = {
        page: pageNumber,
        limit: pageLimit,
        filter: normalizedFilter,
        sort: loadOptions.sort,
        url: '/batch-headers',
      };

      const minDateFound = dxFilterKeyExist("work_min_date", payload.filter);
      const maxDateFound = dxFilterKeyExist("work_max_date", payload.filter);

      if ((this.filters.work_min_date || this.filters.work_max_date) && (!minDateFound || !maxDateFound)) {
        if (!payload.filter) {
          payload.filter = [];
        }

        if (payload.filter.length > 0) {
          payload.filter.push("and");
        }

        if (this.filters.work_min_date) {
          payload.filter.push(["work_min_date", ">=", this.filters.work_min_date]);
          if (this.filters.work_max_date) {
            payload.filter.push("and");
          }
        }

        if (this.filters.work_max_date) {
          payload.filter.push(["work_max_date", "<=", this.filters.work_max_date]);
        }
      }

      await this.getInputs(payload).then((response) => {
        dataSource.data = response.data;
        dataSource.totalCount = response.pagination?.total ?? response.data.length;
      });

      return dataSource;
    },
    async remove(key) {
      const instance = this.$refs?.workInputsGrid?.instance;
      const items = instance?.getDataSource()?.items?.() || [];
      const row = items.find((item) => item.uuid === key);
      if (!row) return;
      await this.deleteInput({
        uuid: row.uuid,
        batch_uuid: row.batch_uuid,
        department_uuid: row.department_uuid,
      });
    },
    onSelectionChanged(e) {
      this.selectedInputUuids = Array.isArray(e.selectedRowKeys) ? e.selectedRowKeys : [];

      if (this.isRestoringSelection) {
        return;
      }

      const instance = this.$refs?.workInputsGrid?.instance;
      const items = instance?.getDataSource()?.items?.() || [];
      const currentSelected = Array.isArray(e?.currentSelectedRowKeys) ? e.currentSelectedRowKeys : [];
      const currentDeselected = Array.isArray(e?.currentDeselectedRowKeys) ? e.currentDeselectedRowKeys : [];

      currentSelected.forEach((key) => {
        if (this.grid_data_uuids.includes(key) && !this.selectedInputUuidsAll.includes(key)) {
          this.selectedInputUuidsAll.push(key);
        }

        const row = items.find((item) => item.uuid === key);
        if (row) {
          this.selectedInputRowsByUuid[key] = row;
        }
      });

      currentDeselected.forEach((key) => {
        if (this.grid_data_uuids.includes(key)) {
          const index = this.selectedInputUuidsAll.indexOf(key);
          if (index !== -1) {
            this.selectedInputUuidsAll.splice(index, 1);
          }
        }

        if (this.selectedInputRowsByUuid[key]) {
          delete this.selectedInputRowsByUuid[key];
        }
      });
    },
    onRowClick(e) {
      if (!e || !e.component || e.rowType !== "data") return;
      const key = e.key;
      const instance = e.component;
      if (instance.isRowSelected(key)) {
        instance.deselectRows([key]);
        return;
      }
      instance.selectRows([key], true);
    },
    onContentReady() {
      const instance = this.$refs?.workInputsGrid?.instance;
      if (instance) {
        const items = instance.getDataSource()?.items?.() ?? [];
        this.grid_data_uuids = Array.isArray(items) ? items.map((item) => item.uuid) : [];
        items.forEach((item) => {
          if (this.selectedInputUuidsAll.includes(item.uuid)) {
            this.selectedInputRowsByUuid[item.uuid] = item;
          }
        });
      }
      this.restoreSelection();
    },
    restoreSelection() {
      const instance = this.$refs?.workInputsGrid?.instance;
      if (!instance) {
        return;
      }

      this.isRestoringSelection = true;
      this.selectedInputUuids = this.selectedInputUuidsAll.filter((uuid) => this.grid_data_uuids.includes(uuid));
      instance.selectRows(this.selectedInputUuids, false);
      this.$nextTick(() => {
        this.isRestoringSelection = false;
      });
    },
    onSelectionCleared() {
      this.selectedInputUuids = [];
      this.selectedInputUuidsAll = [];
      this.selectedInputRowsByUuid = {};
      const instance = this.$refs?.workInputsGrid?.instance;
      if (instance) {
        instance.selectRows([], false);
      }
    },
    async deleteSelectedInputs(uuids) {
      const selectedRows = Array.isArray(uuids)
        ? uuids.map((uuid) => this.selectedInputRowsByUuid[uuid]).filter((row) => !!row)
        : [];
      const batchUuids = Array.from(
        new Set(selectedRows.map((row) => row?.batch_uuid).filter((batchUuid) => !!batchUuid))
      );

      if (!batchUuids.length) {
        return { cancelled: true };
      }

      return bulkAction(this, {
        endpoint: "/batch/bulkDelete",
        action: "delete",
        uuids: batchUuids,
        emptyMessage: this.$t("bulk_actions.no_selection"),
        successMessageBuilder: (resp) => {
          return this.$t("bulk_actions.deleted_summary", {
            deleted: resp?.data?.deleted ?? resp?.deleted ?? batchUuids.length,
          });
        },
      });
    },
    onBulkDeleteSuccess() {
      this.reloadGrid();
    },
    reloadGrid() {
      const instance = this.$refs?.workInputsGrid?.instance;
      if (instance) {
        instance.getDataSource()?.reload?.();
      }
    },
    onExporting(e) {
      e.fileName = "Inputs_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      if (e.format !== "xlsx") return;
      e.cancel = true;

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Inputs");
      const rowMap = new Map();
      let headerRowIndex = null;
      let firstHeaderColIndex = null;

      const detailCols = [
        { caption: this.$t("common.name"), dataField: "parcel.name" },
        { caption: this.$t("common.surface"), dataField: "net_surface_worked" },
        { caption: this.$t("common.quantity"), dataField: "_dose_value" },
      ];

      exportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
        customizeCell: ({ gridCell, excelCell }) => {
          if (!gridCell) return;
          if (gridCell.rowType === "header" && gridCell.column && !headerRowIndex) {
            headerRowIndex = excelCell.row;
            firstHeaderColIndex = excelCell.column;
          }
          if (gridCell.rowType === "data" && gridCell.column && gridCell.column.dataField === "created_at") {
            rowMap.set(excelCell.row, gridCell.data);
          }
        },
      }).then(() => {
        const firstCol = firstHeaderColIndex || 1;
        const insertAt = worksheet.columnCount + 1;

        for (let j = 0; j < detailCols.length; j++) {
          worksheet.spliceColumns(insertAt + j, 0, []);
        }

        if (headerRowIndex) {
          const headerRow = worksheet.getRow(headerRowIndex);
          detailCols.forEach((c, j) => {
            const cell = headerRow.getCell(insertAt + j);
            cell.value = c.caption;
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.font = { bold: true };
          });
        }

        const rowEntriesDesc = Array.from(rowMap.entries()).sort((a, b) => b[0] - a[0]);
        const widthStats = detailCols.map((c) => (c.caption ? String(c.caption).length : 10));
        const lastCol = worksheet.columnCount;
        const detailFill = { type: "pattern", pattern: "solid", fgColor: { argb: "f1f1f1" } };

        for (const [rowIdx, master] of rowEntriesDesc) {
          const details = this.getAffectedParcels(master);
          const count = Array.isArray(details) ? details.length : 0;
          if (!count) continue;

          const blanks = new Array(count).fill([]);
          worksheet.spliceRows(rowIdx + 1, 0, ...blanks);

          for (let i = 0; i < count; i++) {
            const detail = details[i] || {};
            const excelRow = worksheet.getRow(rowIdx + 1 + i);

            for (let c = 1; c <= lastCol; c++) {
              excelRow.getCell(c).fill = detailFill;
            }

            for (let j = 0; j < detailCols.length; j++) {
              const col = detailCols[j];
              let val = null;
              if (col.dataField) {
                const path = col.dataField.split(".");
                val = path.reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), detail);
              }
              const cell = excelRow.getCell(insertAt + j);
              cell.value = val;
              cell.alignment = { horizontal: "center", vertical: "middle" };
              if (typeof val === "number") {
                cell.numFmt = "0.####";
              }
              const repr = val != null ? String(val) : "";
              if (repr.length > widthStats[j]) widthStats[j] = repr.length;
            }
          }
        }

        for (let j = 0; j < detailCols.length; j++) {
          const col = worksheet.getColumn(insertAt + j);
          const targetWidth = Math.max(10, Math.min(40, (widthStats[j] || 10) + 2));
          col.width = targetWidth;
        }

        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
        });
      });
    },
  },
};
</script>
